import {
  FilterOperator,
  QSetStatus,
  useExecuteQSetCheckSimilarityLazyQuery,
  useSearchQSetsLazyQuery,
} from '@exam-management/graphql';
import { QSetPublicationModal } from '@exam-management/qset-publication';
import { useNavigate, useParams } from '@exam-management/router';
import { useLogger } from '@shared/logger';
import { useSnackbar } from '@shared/snackbar';
import type { PropsWithChildren } from '@shared/utils';
import { createContext, useCallback, useEffect, useMemo, useState } from 'react';

import {
  GenerateQSetModal,
  QSetSimilarityCheckModal,
  SelectQSetToCheckPublishedResultsModal,
} from '../../containers';
import { useQSetListSearchParams, useQSetSimilarityCheck } from '../../hooks';
import {
  type CheckQSetDraftToPublishDuplicationRateFormValues,
  type CheckQSetDuplicationRateFormValues,
} from '../../types';
import { getQSetsDraftAndPublishedDetails } from '../../utils/get-qsets-draft-and-published-details';

export enum QSetListModalAction {
  GENERATE_QSET = 'GENERATE_QSET',
  CHECK_DUPLICATE = 'CHECK_DUPLICATE',
  CHECK_PUBLISHED = 'CHECK_PUBLISHED',
  QSET_PUBLISH = 'QSET_PUBLISH',
  QSET_DUPLICATE = 'QSET_DUPLICATE',
  QSET_EXPORT = 'QSET_EXPORT',
  QSET_DELETE = 'QSET_DELETE',
}

interface QSetListContextData {
  selectedQSetIds: string[];
  onSelectQSetIds: (qSetIds: string[]) => void;
  setAction: (action: QSetListModalAction | null) => void;
}

const QSetListContext = createContext<QSetListContextData | undefined>(undefined);

export default function QSetListModalProvider(props: Readonly<PropsWithChildren>) {
  const { children } = props;

  const [selectedQSetIds, setSelectedQSetIds] = useState<string[]>([]);
  const [action, setAction] = useState<QSetListModalAction | null>(null);

  const { showSnackbar } = useSnackbar();

  const navigate = useNavigate();
  const { courseId } = useParams('/exam-courses/:courseId/qsets');

  const logger = useLogger();

  const [{ status }, { changeStatus }] = useQSetListSearchParams();

  const showErrorSnackbar = (message: string) => {
    showSnackbar({ message, variant: 'error' });
  };

  const handleSelectQSetIds = useCallback(
    (qSetIds: string[]) => {
      setSelectedQSetIds(qSetIds);
    },
    [setSelectedQSetIds],
  );

  const handleModalClose = useCallback(() => {
    setAction(null);
  }, [setAction]);

  const handleSetAction = useCallback(
    (action: QSetListModalAction | null) => {
      setAction(action);
    },
    [setAction],
  );

  const [searchQSets] = useSearchQSetsLazyQuery();

  const [executeQSetCheckSimilarity] = useExecuteQSetCheckSimilarityLazyQuery();

  const [execute] = useQSetSimilarityCheck();

  const submitToCheckDuplicate = useCallback(
    async (formData: CheckQSetDuplicationRateFormValues) => {
      await execute(selectedQSetIds, {
        onCompleted: (data, draftQSets, publishedQSets) => {
          navigate('/exam-courses/:courseId/qsets/similarity-check/results', {
            params: {
              courseId,
            },
            state: {
              id: data.id,
              checkType: 'DRAFT_TO_DRAFT',
              courseId,
              duplicationRate: formData.duplicationRate,
              similarityResult: data.qSetSimilarityResults,
              draftQSets,
              publishedQSets,
            },
          });
          setSelectedQSetIds([]);
        },
        onError: () => {
          showErrorSnackbar('Unable to check for duplicates. Please try again.');
        },
      });
    },
    [logger, showErrorSnackbar, navigate, courseId],
  );

  const submitToCheckDraftPublishedDuplicate = useCallback(
    async (formData: CheckQSetDraftToPublishDuplicationRateFormValues) => {
      const { data: searchedQSets } = await searchQSets({
        variables: {
          searchQSetsInput: {
            paging: {
              // NOTE: page size is set to 200 to get all qsets
              pageSize: 200,
              currentPage: 0,
            },
            filter: {
              conditions: [
                {
                  field: 'id',
                  operator: FilterOperator.In,
                  value: [...formData.selectedPublishedQSetIds, ...selectedQSetIds],
                },
              ],
            },
          },
        },
        onError(error) {
          logger.error(error);
          showErrorSnackbar('Unable to search for qsets. Please try again.');
        },
      });

      if (!searchedQSets?.excSearchQSets.qSets) return;

      const { draftQSets, publishedQSets } = getQSetsDraftAndPublishedDetails(
        searchedQSets.excSearchQSets.qSets,
      );

      const { data } = await executeQSetCheckSimilarity({
        variables: {
          input: {
            qSetsA: draftQSets.map((qset) => ({
              id: qset.id,
              code: qset.code,
              updatedAt: qset.updatedAt,
            })),
            qSetsB: publishedQSets.map((qset) => ({
              id: qset.id,
              code: qset.code,
              updatedAt: qset.updatedAt,
            })),
          },
        },
        onError(error) {
          logger.error(error);
          showErrorSnackbar('Unable to check for duplicates. Please try again.');
        },
      });

      if (data?.excGetQSetSimilarities) {
        navigate('/exam-courses/:courseId/qsets/similarity-check/results', {
          params: {
            courseId,
          },
          state: {
            id: data.excGetQSetSimilarities.id,
            checkType: 'DRAFT_TO_PUBLISHED',
            courseId,
            duplicationRate: formData.duplicationRate,
            similarityResult: data.excGetQSetSimilarities.qSetSimilarityResults,
            draftQSets,
            publishedQSets,
          },
        });
        setSelectedQSetIds([]);
      }
    },
    [logger, showErrorSnackbar, navigate, selectedQSetIds, searchQSets, executeQSetCheckSimilarity],
  );

  const handlePublishQSetsCompleted = useCallback(async () => {
    changeStatus(QSetStatus.Published);
    setSelectedQSetIds([]);
  }, []);

  const isOpenModal = useMemo(() => action !== null, [action]);

  const contextValue = useMemo(
    () => ({
      selectedQSetIds,
      onSelectQSetIds: handleSelectQSetIds,
      setAction: handleSetAction,
    }),
    [selectedQSetIds, handleSelectQSetIds, handleSetAction],
  );

  useEffect(() => {
    handleSelectQSetIds([]);
  }, [status]);

  return (
    <QSetListContext.Provider value={contextValue}>
      {children}
      {isOpenModal ? (
        <>
          <GenerateQSetModal
            open={action === QSetListModalAction.GENERATE_QSET}
            onClose={handleModalClose}
          />
          <QSetSimilarityCheckModal
            open={action === QSetListModalAction.CHECK_DUPLICATE}
            onClose={handleModalClose}
            onFormSubmit={submitToCheckDuplicate}
          />
          <SelectQSetToCheckPublishedResultsModal
            open={action === QSetListModalAction.CHECK_PUBLISHED}
            onClose={handleModalClose}
            onFormSubmit={submitToCheckDraftPublishedDuplicate}
          />
          <QSetPublicationModal
            open={action === QSetListModalAction.QSET_PUBLISH}
            onClose={handleModalClose}
            qSetIds={selectedQSetIds}
            onCompleted={handlePublishQSetsCompleted}
          />
        </>
      ) : null}
    </QSetListContext.Provider>
  );
}

export { QSetListContext };
